import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenIcon } from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import JournalPicker from './JournalPicker';
import OtableFileStart from './OtableFileStart';

const MENU_PROPS = { MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } };
const DEFAULT_TEMPLATE = 'original_research';

// Section template -> publication_type stored on the document
const TEMPLATE_PUBLICATION_TYPE = {
  conference_paper: 'conference_paper',
  book_chapter: 'book_chapter',
};

/**
 * Start a new publication, journal first: the chosen journal sets the citation style
 * and recommends the section template (both can be changed later).
 * defaults: { title, target_journal } from the consortium's publication collaboration.
 * onCreate(payload) should resolve once the publication exists; onCreateFromFile(file), when given, starts it from an
 * Otable document (.otable) instead (otableFile.js).
 */
const CreatePublicationDialog = ({ open, onClose, onCreate, onCreateFromFile, defaults }) => {
  const [fileBusy, setFileBusy] = useState(false);
  const [fileError, setFileError] = useState('');
  const [title, setTitle] = useState('');
  const [journal, setJournal] = useState({ title: '', styleId: null, styleTitle: '' });
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [saving, setSaving] = useState(false);

  // Reset only when the dialog opens; `defaults` is rebuilt on every parent render
  useEffect(() => {
    if (!open) return;
    setTitle(defaults?.title || '');
    setJournal({ title: defaults?.target_journal || '', styleId: null, styleTitle: '' });
    setDetails(null);
    setTemplate(DEFAULT_TEMPLATE);
    setFileError('');
    axiosService.get('/proposal/proposals/templates/', { params: { document_type: 'publication' } })
      .then((res) => setTemplates((res.data || []).filter((t) => !t.is_user_defined)))
      .catch(() => setTemplates([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Journal style details: author guidelines and the recommended section template
  useEffect(() => {
    if (!journal.styleId) { setDetails(null); return undefined; }
    let cancelled = false;
    setLoadingDetails(true);
    axiosService.get(`/proposal/journal-styles/${encodeURIComponent(journal.styleId)}/`)
      .then((res) => {
        if (cancelled) return;
        setDetails(res.data);
        if (res.data?.recommended_template) setTemplate(res.data.recommended_template);
      })
      .catch(() => { if (!cancelled) setDetails(null); })
      .finally(() => { if (!cancelled) setLoadingDetails(false); });
    return () => { cancelled = true; };
  }, [journal.styleId]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await onCreate({
        title,
        target_journal: journal.title,
        citation_style: journal.styleId || 'vancouver',
        citation_style_title: journal.styleId ? journal.styleTitle : '',
        section_template: template,
        publication_type: TEMPLATE_PUBLICATION_TYPE[template] || 'journal_article',
      });
    } finally {
      setSaving(false);
    }
  };

  const recommended = details?.recommended_template;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start a Publication</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose the target journal first: its reference style is applied from the start and the section outline follows
          the journal’s usual article structure. All consortium members are added as authors.
        </Typography>
        <Grid container spacing={2}>


        <Grid item xs={12}>
            <TextField label="Working title" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} />
          </Grid>
          
          <Grid item xs={12}>
            <JournalPicker
              autoFocus
              value={journal}
              onChange={setJournal}
              helperText="Search 10,000+ journals (Zotero Style Repository), or type any journal name"
            />
          </Grid>

          {journal.styleId && (
            <Grid item xs={12}>
              {loadingDetails ? <CircularProgress size={18} /> : details && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" color="primary" variant="outlined" label={`Reference style: ${journal.styleTitle}`} />
                  {details.documentation_url && (
                    <Link href={details.documentation_url} target="_blank" rel="noopener noreferrer" variant="body2"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      Author guidelines <OpenIcon sx={{ fontSize: 14 }} />
                    </Link>
                  )}
                  {details.supported === false && (
                    <Alert severity="warning" sx={{ py: 0 }}>This journal uses footnote citations, which are not supported yet.</Alert>
                  )}
                </Stack>
              )}
            </Grid>
          )}
          {!journal.styleId && journal.title && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Journal not from the list: the Vancouver reference style is used; you can change it later.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField select fullWidth label="Article type / section outline" value={template}
              onChange={(e) => setTemplate(e.target.value)} SelectProps={MENU_PROPS}
              helperText={recommended ? 'Pre-selected for this journal' : 'Sections can be changed later with “Select Template”'}>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}{t.id === recommended ? ' — recommended for this journal' : ''}
                </MenuItem>
              ))}
              {!templates.length && <MenuItem value={DEFAULT_TEMPLATE}>Original Research Article</MenuItem>}
            </TextField>
          </Grid>

        
        </Grid>
        {onCreateFromFile && (
          <OtableFileStart kind="publication" busy={fileBusy} error={fileError} onFile={async (file) => {
            setFileBusy(true); setFileError('');
            try { await onCreateFromFile(file); } catch (err) { setFileError(err.message); } finally { setFileBusy(false); }
          }} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving || fileBusy || !title.trim()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePublicationDialog;
